import { Injectable } from "@angular/core";
import { IconListItem } from "@shared/models/icon-list-item.model";
import { Product, ProductActions } from "@shared/models/products.model";

import { findIndex, flatten } from "lodash";
import { Observable, of, scheduled, asyncScheduler } from "rxjs";

import { ServerSettingsService } from "../server-settings/server-settings.service";
import { ServerSettingsForProduct } from "./actions/server-settings-for-product";
import { map, switchMap } from "rxjs/operators";
import { ApiService } from "../api/api.service";
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class ProductsFilterService {
  private enableBlockedProducts = false;

  constructor(
    private serverSettingsService: ServerSettingsService,
    private apiService: ApiService
  ) {}

  /**
   * Проверка доступности действий с продуктом
   */
  public checkActionsAvailability(product: Product, productActions: ProductActions): Observable<ProductActions> {
    const serverSettingsForProduct = this.getServerSettingsForProduct(product.productType);
    const allSettingsNames = flatten(serverSettingsForProduct.map(item => item.settingsNames));
    const search = `number_of_entries=1000&list_of_statuses=send`;
    let requestCloseCard$ = scheduled(of(null), asyncScheduler);

    if (product.isDebitCard) {
      requestCloseCard$ = this.apiService.post(`rest/personal/last_documents/doc_types?${search}`, {
        docTypes: [
          {docModule: 'ibankfl', docType: 'request_close_card'}
        ],
        dateFrom: dayjs()
          .subtract(46, 'days')
          .format('YYYYMMDD'),
        dateTo: dayjs().format('YYYYMMDD')
      }).pipe(
        map((documents: any) => {
          if (documents && documents.lastDocument) {
            const idx = productActions.infoActions.findIndex((action) => action.type === 'productClose');

            if (idx !== -1) {
              productActions.infoActions.splice(idx, 1);
            }

            return null;
          }
        })
      );
    }

    const searchAndHandling = (
      actions: IconListItem[],
      settingForProduct: ServerSettingsForProduct,
      // eslint-disable-next-line @typescript-eslint/ban-types
      settingsObject: Object
    ): boolean => {
      const actionIndex = findIndex(actions, action => action.type === settingForProduct.typeAction);

      if (actionIndex !== -1) {
        const allowed: boolean = settingForProduct.settingsNames.reduce(
          (accum, value) => accum || settingsObject[value], false
        );

        if (!allowed) actions.splice(actionIndex, 1);

        return true; // Смог найти и обработать
      }

      return true; // Не нашел
    };

    const filteredActionsByServerSettings$ = this.serverSettingsService.getBooleanSettings(
      this.serverSettingsService.servicesFL,
      allSettingsNames
    ).pipe(
      map(res => {
        serverSettingsForProduct.forEach(item => {
          if (!searchAndHandling(productActions.actions, item, res)) {
            searchAndHandling(productActions.infoActions, item, res);
          }
        });

        return productActions;
      })
    );

    return requestCloseCard$.pipe(
      switchMap(() => filteredActionsByServerSettings$)
    );
  }

  /**
   * Проверка возможности снятия со вклада
   */
  public checkAvailableDebitFromDeposit(product: Product): boolean {
    return product.depositIsRemove && product.depositIsRemove !== 'false';
  }

  /**
   * Проверка возможности "платежа или перевода"
   */
  public checkPaymentOrTransferPossibility(product: Product): boolean {
    const statusNew = product.status === 'new';
    const notZeroBalance = +product.balance > 0;
    const currencyCode = product.currencyCode === '810' || product.currencyCode === '643';
    const isEsAccount = this.checkESAccount(product.number); // Проверка счета эскроу
    const isBrokerAccount = product.isBrokerAccount; // Проверка на брокерский счет
    const isMetallAccount = product.isMetallAccount;
    const deposit =
      product.productType === 'deposit'
        ? !((product as any).depositIsRemove && (product as any).depositIsRemove === 'false')
        : true;
    const deposit2 = product.productType === 'deposit' ? +product.balance > +product.limit : true;

    return (
      statusNew &&
      notZeroBalance &&
      currencyCode &&
      deposit &&
      deposit2 &&
      !isEsAccount &&
      !isBrokerAccount &&
      !isMetallAccount
    );
  }

  /**
   * Проверка возможности "пополнения"
   */
  public checkReplenishmentPossibility(product: Product): boolean {
    const statusNew = product.status === 'new';
    const oneProduct = false; // Предполагаем, что у нас есть больше одного продукта
    const isEsAccount = this.checkESAccount(product.number); // Проверка счета эскроу
    const isMetallAccount = product.isMetallAccount;
    const deposit =
      product.productType === 'deposit'
        ? (product as any).depositIsMove
          ? (product as any).depositIsMove === 'true'
          : true
        : true;

    return statusNew && !oneProduct && deposit && !isEsAccount && !isMetallAccount;
  }

  /**
   * Фильтрация по настройкам
   */
  public filteringBySetting(product: Product): boolean {
    if (this.enableBlockedProducts) return true;

    return this.filteringByStatusNew(product);
  }

  /**
   * Фильтрация по статусу "new"
   */
  public filteringByStatusNew(product: Product): boolean {
    return product.status !== 'block';
  }

  /**
   * Установка флага отображения заблокированных продуктов
   */
  public setEnableBlockedProducts(value: boolean): void {
    this.enableBlockedProducts = value;
  }

  /**
   * Проверка на ES-счет
   */
  public checkESAccount(number: string): boolean {
    return /^40724/.test(number);
  }


  /**
   * Получение настроек сервера для продукта
   */
  private getServerSettingsForProduct(productType: string): ServerSettingsForProduct[] {
    // Реализация метода
    return [];
  }
}
